import React from 'react'
import PropTypes from 'prop-types'
import ChevronIcon from '../../icons/chevron_icon'
import DropdownItem from './children/dropdown_item'
import UtilityBelt from '@thebeansgroup/utility-belt'
import hasDom from 'has-dom'
import styles from './styles'
import {
  valueIsAvailable,
  canDisplayPlaceholder,
  getAvailableOptions,
  autoCapitaliseValue,
  hasAvailableOptions,
  validOptionFocused,
  getInputType
} from './helpers'
import {
  OUT_OF_RANGE_DROPDOWN_ITEM_INDEX,
  DROPDOWN_KEY_CODES
} from './constants'

const SyntheticEvent = UtilityBelt.Events.SyntheticEvent

class Dropdown extends React.Component {
  constructor (props) {
    super(props)
    this.state = {
      focused: false,
      dropdownOpen: false,
      dropdownItemFocused: OUT_OF_RANGE_DROPDOWN_ITEM_INDEX,
      mouseOverDropdown: false
    }
    this.bindHandlers()
  }

  bindHandlers () {
    this.setContainerRef = this.setContainerRef.bind(this)
    this.setInputRef = this.setInputRef.bind(this)
    this.handleMouseOverDropdownItem = this.handleMouseOverDropdownItem.bind(this)
    this.handleMouseOutDropdownItem = this.handleMouseOutDropdownItem.bind(this)
    this.handleChange = this.handleChange.bind(this)
    this.handleFocus = this.handleFocus.bind(this)
    this.handleBlur = this.handleBlur.bind(this)
    this.handlePlaceholderClick = this.handlePlaceholderClick.bind(this)
    this.handlePlaceholderMouseDown = this.handlePlaceholderMouseDown.bind(this)
    this.handleContainerClick = this.handleContainerClick.bind(this)
    this.handleKeyPress = this.handleKeyPress.bind(this)
    this.handleEnterKeyPress = this.handleEnterKeyPress.bind(this)
    this.handleArrowDownKeyPress = this.handleArrowDownKeyPress.bind(this)
    this.handleArrowUpKeyPress = this.handleArrowUpKeyPress.bind(this)
    this.handleMouseOverDropdown = this.handleMouseOverDropdown.bind(this)
    this.handleMouseOutDropdown = this.handleMouseOutDropdown.bind(this)
  }

  setContainerRef (node) {
    this.containerRef = node
  }

  setInputRef (node) {
    this.inputRef = node
  }

  componentDidMount () {
    if (hasDom()) {
      window.addEventListener('keydown', this.handleKeyPress, false)
    }
  }

  componentWillUnmount () {
    if (hasDom()) {
      window.removeEventListener('keydown', this.handleKeyPress)
    }
  }

  handleMouseOverDropdown () {
    this.setState({ mouseOverDropdown: true })
  }

  handleMouseOutDropdown () {
    this.setState({ mouseOverDropdown: false })
  }

  handlePlaceholderClick () {
    // Click 'through' the placeholder to the input
    this.inputRef.focus()
  }

  handlePlaceholderMouseDown (e) {
    // Disable click events so you can't highlight the placeholder text
    e.preventDefault()
  }

  handleKeyPress (e) {
    if (!this.state.focused) return
    const keyCode = new SyntheticEvent(e).keyCode
    const actionName = DROPDOWN_KEY_CODES[keyCode]

    const actions = {
      Enter: this.handleEnterKeyPress,
      ArrowDown: this.handleArrowDownKeyPress,
      ArrowUp: this.handleArrowUpKeyPress
    }

    const action = actions[actionName]
    if (action) action(e)
  }

  handleEnterKeyPress (e) {
    e.preventDefault()
    this.inputRef.blur()
  }

  handleArrowDownKeyPress (e) {
    e.preventDefault()
    this.setState({
      dropdownItemFocused: Math.min(
        getAvailableOptions(this.props).length - 1,
        this.state.dropdownItemFocused + 1
      )
    })
  }

  handleArrowUpKeyPress (e) {
    e.preventDefault()
    this.setState({
      dropdownItemFocused: Math.max(0, this.state.dropdownItemFocused - 1)
    })
  }

  handleContainerClick () {
    if (this.props.disabled) return
    if (this.state.focused) {
      this.handleBlur()
    } else {
      this.openDropdown()
    }
  }

  openDropdown () {
    this.inputRef.focus()
    this.setState({
      focused: true,
      dropdownOpen: true
    })
    if (this.props.autocomplete) this.props.valueChangeHandler('')
  }

  closeDropdown () {
    this.inputRef.blur()
    this.setState({
      focused: false,
      dropdownOpen: false,
      dropdownItemFocused: OUT_OF_RANGE_DROPDOWN_ITEM_INDEX
    })
  }

  handleFocus () {
    if (!this.state.mouseOverDropdown) this.openDropdown()
  }

  handleBlur () {
    if (validOptionFocused(this.state)) {
      const newValue = getAvailableOptions(this.props)[this.state.dropdownItemFocused]
      this.props.valueChangeHandler(newValue)
    } else {
      const newValue = valueIsAvailable(this.props) ? this.props.value : ''
      this.props.valueChangeHandler(newValue)
    }
    this.closeDropdown()
  }

  handleMouseOverDropdownItem (index) {
    this.setState({ dropdownItemFocused: index })
  }

  handleMouseOutDropdownItem () {
    this.setState({ dropdownItemFocused: OUT_OF_RANGE_DROPDOWN_ITEM_INDEX })
  }

  handleChange (e) {
    const value = autoCapitaliseValue(this.props, e.target.value)
    this.props.valueChangeHandler(value)
    this.setState({ dropdownOpen: hasAvailableOptions(this.props, value) })
  }

  renderOptions () {
    const options = getAvailableOptions(this.props)
    return options.map((value, i) => {
      return (
        <DropdownItem
          key={`dropdownItem_${i}`}
          index={i}
          value={value}
          onMouseOver={this.handleMouseOverDropdownItem}
          onMouseOut={this.handleMouseOutDropdownItem}
          hover={this.state.dropdownItemFocused === i}
          active={this.props.value === value}
        />
      )
    })
  }

  renderDropdown () {
    if (!this.state.dropdownOpen) return
    return (
      <ul
        style={{
          ...styles.dropdownList.base,
          ...styles.dropdownList[this.props.type]
        }}
      >
        {this.renderOptions()}
      </ul>
    )
  }

  renderPlaceholder () {
    if (!canDisplayPlaceholder(this.props, this.state)) return
    return (
      <span
        style={styles.placeholder}
        onClick={this.handlePlaceholderClick}
        onMouseDown={this.handlePlaceholderMouseDown}
      >
        {this.props.placeholder}
      </span>
    )
  }

  renderInput () {
    return (
      <input
        ref={this.setInputRef}
        style={{
          ...styles.dropdown.base,
          ...this.state.dropdownOpen && styles.dropdown.open,
          ...styles.dropdown[this.props.type].base,
          ...this.props.disabled && styles.dropdown[this.props.type].disabled
        }}
        disabled={this.props.disabled}
        value={this.props.value}
        type={getInputType(this.props)}
        onChange={this.handleChange}
      />
    )
  }

  renderChevron () {
    return (
      <ChevronIcon
        style={styles.icon.base}
        colour={{
          ...styles.icon[this.props.type],
          ...this.props.disabled && styles.icon.disabled
        }.color}
        width={styles.icon.base.width}
        height={styles.icon.base.height}
        rotateDegrees={this.state.dropdownOpen ? 270 : 90}
      />
    )
  }

  renderIcon () {
    return (
      <div
        style={{
          ...styles.iconContainer.base,
          ...styles.iconContainer[this.props.type].base,
          ...this.props.disabled && styles.iconContainer[this.props.type].disabled
        }}
      >
        {this.renderChevron()}
      </div>
    )
  }

  render () {
    return (
      <div
        ref={this.setContainerRef}
        style={{
          ...styles.container.base,
          ...this.props.disabled && styles.container.disabled
        }}
        onClick={this.handleContainerClick}
        onBlur={this.handleBlur}
        onFocus={this.handleFocus}
        onMouseOver={this.handleMouseOverDropdown}
        onMouseOut={this.handleMouseOutDropdown}
      >
        {this.renderInput()}
        {this.renderPlaceholder()}
        {this.renderIcon()}
        {this.renderDropdown()}
      </div>
    )
  }
}

Dropdown.defaultProps = {
  type: 'alpha',
  autocomplete: false,
  disabled: false
}

Dropdown.propTypes = {
  value: PropTypes.string,
  valueChangeHandler: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  options: PropTypes.array.isRequired,
  type: PropTypes.oneOf(['alpha', 'bravo']),
  autocomplete: PropTypes.bool,
  disabled: PropTypes.bool
}

export default Dropdown
